// Application Timeout System - 2 Week Auto-Decline Feature
// This handles the automatic declining of applications after 2 weeks if student doesn't respond
import React, { useState} from 'react';
import { 
  doc, 
  updateDoc, 
  addDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../configs/firebase';
import { 
  FaClock
} from 'react-icons/fa';

// Constants
const TIMEOUT_DURATION = 14 * 24 * 60 * 60 * 1000; // 14 days in milliseconds
const REMINDER_TIMING = [7, 3, 1]; // Send reminders at 7, 3, and 1 days before timeout

// Enhanced Application Status Management
export const APPLICATION_STATUSES = {
  PENDING: 'pending',
  UNDER_REVIEW: 'under_review',
  INTERVIEW: 'interview',
  ACCEPTED: 'accepted',
  STUDENT_ACCEPTED: 'student_accepted',
  STUDENT_DECLINED: 'student_declined',
  REJECTED: 'rejected',
  WITHDRAWN: 'withdrawn',
  TIMEOUT_DECLINED: 'timeout_declined'
};

// Update application status with timeout tracking
export const updateApplicationStatusWithTimeout = async (applicationId, newStatus, employerId, additionalData = {}) => {
  try {
    const updateData = {
      status: newStatus,
      updatedAt: serverTimestamp(),
      ...additionalData
    };

    // If accepting application, set timeout deadline
    if (newStatus === APPLICATION_STATUSES.ACCEPTED) {
      const timeoutDate = new Date(Date.now() + TIMEOUT_DURATION);
      updateData.acceptedAt = serverTimestamp();
      updateData.studentResponseDeadline = timeoutDate.toISOString();
      updateData.timeoutEnabled = true;
    }

    // If student responds (accept/decline), disable timeout
    if (newStatus === APPLICATION_STATUSES.STUDENT_ACCEPTED || 
        newStatus === APPLICATION_STATUSES.STUDENT_DECLINED) {
      updateData.timeoutEnabled = false;
      updateData.studentResponseAt = serverTimestamp();
    }

    await updateDoc(doc(db, 'applications', applicationId), updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
};

// Student response functions
export const studentAcceptOffer = async (applicationId, studentId) => {
  try {
    const updateData = {
      status: APPLICATION_STATUSES.STUDENT_ACCEPTED,
      timeoutEnabled: false,
      studentResponseAt: serverTimestamp(),
      studentDecision: 'accepted'
    };

    await updateDoc(doc(db, 'applications', applicationId), updateData);

    return { success: true };
  } catch (error) {
    console.error('Error accepting offer:', error);
    throw error;
  }
};

export const studentDeclineOffer = async (applicationId, studentId, reason = '') => {
  try {
    const updateData = {
      status: APPLICATION_STATUSES.STUDENT_DECLINED,
      timeoutEnabled: false,
      studentResponseAt: serverTimestamp(),
      studentDecision: 'declined',
      declineReason: reason
    };

    await updateDoc(doc(db, 'applications', applicationId), updateData);

    return { success: true };
  } catch (error) {
    console.error('Error declining offer:', error);
    throw error;
  }
};

// Check for timed-out applications (run this as a scheduled function)
export const checkForTimedOutApplications = async () => {
  try {
    const now = new Date();
    
    // Query applications that are accepted and have timeout enabled
    const q = query(
      collection(db, 'applications'),
      where('status', '==', APPLICATION_STATUSES.ACCEPTED),
      where('timeoutEnabled', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const timedOutApplications = [];
    const reminderApplications = [];
    
    querySnapshot.forEach((doc) => {
      const application = { id: doc.id, ...doc.data() };
      const deadline = new Date(application.studentResponseDeadline);
      const timeUntilDeadline = deadline.getTime() - now.getTime();
      const daysUntilDeadline = Math.ceil(timeUntilDeadline / (24 * 60 * 60 * 1000));
      
      // Check if application has timed out
      if (now >= deadline) {
        timedOutApplications.push(application);
      }
      // Check if reminders need to be sent
      else if (REMINDER_TIMING.includes(daysUntilDeadline)) {
        const reminderKey = `${daysUntilDeadline}day`;
        const remindersSent = application.remindersSent || [];
        
        if (!remindersSent.includes(reminderKey)) {
          reminderApplications.push({
            ...application,
            daysUntilDeadline
          });
        }
      }
    });
    
    // Process timed-out applications
    for (const application of timedOutApplications) {
      await updateDoc(doc(db, 'applications', application.id), {
        status: APPLICATION_STATUSES.TIMEOUT_DECLINED,
        timeoutEnabled: false,
        timedOutAt: serverTimestamp()
      });
    }
    
    // Send reminders (you would implement email/notification sending here)
    for (const application of reminderApplications) {
      const reminderKey = `${application.daysUntilDeadline}day`;
      const remindersSent = application.remindersSent || [];
      
      // Update reminders sent
      await updateDoc(doc(db, 'applications', application.id), {
        remindersSent: [...remindersSent, reminderKey]
      });
      
      // Here you would send actual email/notification to student
      console.log(`Reminder sent to ${application.studentEmail}: ${application.daysUntilDeadline} days remaining`);
    }
    
    return {
      timedOutCount: timedOutApplications.length,
      remindersSentCount: reminderApplications.length
    };
    
  } catch (error) {
    console.error('Error checking for timed-out applications:', error);
    throw error;
  }
};

// Get application with timeout info
export const getApplicationWithTimeoutInfo = (application) => {
  if (!application || application.status !== APPLICATION_STATUSES.ACCEPTED) {
    return { ...application, timeoutInfo: null };
  }
  
  const now = new Date();
  const deadline = new Date(application.studentResponseDeadline);
  const timeUntilDeadline = deadline.getTime() - now.getTime();
  const daysUntilDeadline = Math.ceil(timeUntilDeadline / (24 * 60 * 60 * 1000));
  const hoursUntilDeadline = Math.ceil(timeUntilDeadline / (60 * 60 * 1000));
  
  const timeoutInfo = {
    deadline: deadline.toISOString(),
    timeUntilDeadline,
    daysUntilDeadline,
    hoursUntilDeadline,
    isExpired: now >= deadline,
    isUrgent: daysUntilDeadline <= 3,
    isWarning: daysUntilDeadline <= 7,
    formattedDeadline: deadline.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  };
  
  return { ...application, timeoutInfo };
};

// React component for student offer response
export const OfferResponseComponent = ({ application, onResponse, loading }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  
  const applicationWithTimeout = getApplicationWithTimeoutInfo(application);
  const { timeoutInfo } = applicationWithTimeout;
  
  if (application.status !== APPLICATION_STATUSES.ACCEPTED) {
    return null;
  }
  
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-green-900 mb-2">
            🎉 Congratulations! You've been accepted
          </h3>
          <p className="text-green-800 mb-4">
            Your application for <strong>{application.positionTitle}</strong> has been accepted!
          </p>
          
          {/* Timeout Warning */}
          {timeoutInfo && (
            <div className={`p-3 rounded-lg mb-4 ${
              timeoutInfo.isUrgent ? 'bg-red-100 border border-red-200' :
              timeoutInfo.isWarning ? 'bg-yellow-100 border border-yellow-200' :
              'bg-blue-100 border border-blue-200'
            }`}>
              <div className="flex items-center">
                <FaClock className={`mr-2 ${
                  timeoutInfo.isUrgent ? 'text-red-600' :
                  timeoutInfo.isWarning ? 'text-yellow-600' :
                  'text-blue-600'
                }`} />
                <span className={`font-medium ${
                  timeoutInfo.isUrgent ? 'text-red-800' :
                  timeoutInfo.isWarning ? 'text-yellow-800' :
                  'text-blue-800'
                }`}>
                  {timeoutInfo.isUrgent ? 'URGENT: ' : ''}
                  Response required by {timeoutInfo.formattedDeadline}
                </span>
              </div>
              <p className={`text-sm mt-1 ${
                timeoutInfo.isUrgent ? 'text-red-700' :
                timeoutInfo.isWarning ? 'text-yellow-700' :
                'text-blue-700'
              }`}>
                {timeoutInfo.daysUntilDeadline > 0 
                  ? `${timeoutInfo.daysUntilDeadline} day${timeoutInfo.daysUntilDeadline !== 1 ? 's' : ''} remaining`
                  : timeoutInfo.hoursUntilDeadline > 0
                  ? `${timeoutInfo.hoursUntilDeadline} hour${timeoutInfo.hoursUntilDeadline !== 1 ? 's' : ''} remaining`
                  : 'Deadline has passed'
                }
              </p>
            </div>
          )}
          
          <p className="text-sm text-gray-600 mb-4">
            Please respond within the deadline to secure your position. If you don't respond by the deadline, 
            your application will be automatically declined.
          </p>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex space-x-4">
        <button
          onClick={() => onResponse('accept')}
          disabled={loading || timeoutInfo?.isExpired}
          className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Processing...' : 'Accept Offer 🎉'}
        </button>
        
        <button
          onClick={() => setShowDeclineModal(true)}
          disabled={loading || timeoutInfo?.isExpired}
          className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Decline Offer
        </button>
      </div>
      
      {/* Decline Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Decline Offer
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to decline this offer? This action cannot be undone.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for declining (optional)
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                placeholder="Help us improve by sharing why you're declining..."
              />
              <p className="text-xs text-gray-500 mt-1">{declineReason.length}/500 characters</p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onResponse('decline', declineReason);
                  setShowDeclineModal(false);
                }}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700"
              >
                Confirm Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Employer view component for accepted applications
export const EmployerAcceptedApplicationView = ({ application }) => {
  const applicationWithTimeout = getApplicationWithTimeoutInfo(application);
  const { timeoutInfo } = applicationWithTimeout;
  
  if (application.status !== APPLICATION_STATUSES.ACCEPTED) {
    return null;
  }
  
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-yellow-900">Waiting for Student Response</h4>
          <p className="text-sm text-yellow-800">
            Student has until {timeoutInfo?.formattedDeadline} to respond
          </p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          timeoutInfo?.isUrgent ? 'bg-red-100 text-red-800' :
          timeoutInfo?.isWarning ? 'bg-yellow-100 text-yellow-800' :
          'bg-green-100 text-green-800'
        }`}>
          {timeoutInfo?.daysUntilDeadline > 0 
            ? `${timeoutInfo.daysUntilDeadline} days left`
            : timeoutInfo?.hoursUntilDeadline > 0
            ? `${timeoutInfo.hoursUntilDeadline} hours left`
            : 'Expired'
          }
        </div>
      </div>
      
      {timeoutInfo?.isUrgent && (
        <div className="mt-2 text-xs text-red-700">
          ⚠️ Deadline approaching - application will auto-decline if no response
        </div>
      )}
    </div>
  );
};

// Email templates for reminders and notifications
export const EMAIL_TEMPLATES = {
  ACCEPTANCE_NOTIFICATION: {
    subject: "🎉 Congratulations! Your internship application has been accepted",
    template: `Dear {studentName},

Congratulations! We are excited to inform you that your application for the {positionTitle} position at {companyName} has been accepted.

⏰ IMPORTANT: Please respond to this offer by {deadline} to secure your position.

What's next:
• Accept or decline this offer through your dashboard
• If you accept, we'll send you onboarding information
• If you don't respond by {deadline}, the offer will automatically expire

Log in to your account to respond: {dashboardUrl}

We look forward to hearing from you!

Best regards,
{companyName} Team`
  },
  
  REMINDER_7_DAYS: {
    subject: "⏰ Reminder: Respond to your internship offer (7 days remaining)",
    template: `Dear {studentName},

This is a friendly reminder that your internship offer for {positionTitle} at {companyName} requires a response.

You have 7 days remaining to respond (deadline: {deadline}).

Please log in to your dashboard to accept or decline: {dashboardUrl}

Best regards,
{companyName} Team`
  },
  
  REMINDER_3_DAYS: {
    subject: "🚨 URGENT: Respond to your internship offer (3 days remaining)",
    template: `Dear {studentName},

URGENT: Your internship offer for {positionTitle} at {companyName} expires in 3 days.

Deadline: {deadline}

Please respond immediately to avoid losing this opportunity: {dashboardUrl}

Best regards,
{companyName} Team`
  },
  
  REMINDER_1_DAY: {
    subject: "🚨 FINAL NOTICE: Respond to your internship offer (24 hours remaining)",
    template: `Dear {studentName},

FINAL NOTICE: Your internship offer for {positionTitle} at {companyName} expires in 24 hours.

Deadline: {deadline}

This is your last chance to respond. Please log in immediately: {dashboardUrl}

Best regards,
{companyName} Team`
  },
  
  TIMEOUT_NOTIFICATION: {
    subject: "Internship offer expired - No response received",
    template: `Dear {studentName},

Unfortunately, your internship offer for {positionTitle} at {companyName} has expired due to no response being received by the deadline ({deadline}).

If you're still interested in opportunities with us, please feel free to apply for future positions.

Best regards,
{companyName} Team`
  }
};

export default {
  updateApplicationStatusWithTimeout,
  studentAcceptOffer,
  studentDeclineOffer,
  checkForTimedOutApplications,
  getApplicationWithTimeoutInfo,
  OfferResponseComponent,
  EmployerAcceptedApplicationView,
  APPLICATION_STATUSES,
  EMAIL_TEMPLATES
};